// src/components/charting/ToothSummaryCard.tsx
import React from "react";
import "../../css/ToothSummaryCard.css";
import {
  ToothChartingData,
  SegmentData,
} from "../../pages/DentalChartingScreen";
import largeToothBg from "../../assets/large-tooth-bg.png";
import smallToothBg from "../../assets/small-tooth-bg.png";

interface ToothSummaryCardProps {
  toothId: number;
  data?: ToothChartingData;
}

const SMALL_TEETH_IDS = new Set([
  53, 52, 51, 61, 62, 63, 83, 82, 81, 71, 72, 73,
]);

// Coordinates for a LARGE tooth summary
const largeToothSegments = [
  { id: 1, top: "16%", left: "33%" },
  { id: 2, top: "16%", left: "50%" },
  { id: 3, top: "16%", left: "67%" },
  { id: 4, top: "35%", left: "87%" },
  { id: 5, top: "50%", left: "87%" },
  { id: 6, top: "65%", left: "87%" },
  { id: 9, top: "84%", left: "67%" },
  { id: 8, top: "84%", left: "50%" },
  { id: 7, top: "84%", left: "33%" },
  { id: 12, top: "65%", left: "13%" },
  { id: 11, top: "50%", left: "13%" },
  { id: 10, top: "35%", left: "13%" },
  { id: 13, top: "42%", left: "33%" },
  { id: 14, top: "42%", left: "50%" },
  { id: 15, top: "42%", left: "67%" },
];

// Coordinates for a SMALL tooth summary
const smallToothSegments = [
  { id: 1, top: "13%", left: "50%" },
  { id: 2, top: "28%", left: "50%" },
  { id: 3, top: "42%", left: "50%" },
  { id: 4, top: "32%", left: "85%" },
  { id: 5, top: "47%", left: "85%" },
  { id: 6, top: "62%", left: "85%" },
  { id: 9, top: "62%", left: "50%" },
  { id: 8, top: "76%", left: "50%" },
  { id: 7, top: "90%", left: "50%" },
  { id: 12, top: "62%", left: "20%" },
  { id: 11, top: "47%", left: "20%" },
  { id: 10, top: "32%", left: "20%" },
];

const getParameterTypeForSegment = (segmentId: number): keyof SegmentData => {
  if ([1, 4, 7, 10, 13].includes(segmentId)) return "restoration";
  if ([2, 5, 8, 11, 14].includes(segmentId)) return "caries";
  return "activity";
};

const ToothSummaryCard: React.FC<ToothSummaryCardProps> = ({
  toothId,
  data,
}) => {
  const isSmallTooth = SMALL_TEETH_IDS.has(toothId);
  const segments = isSmallTooth ? smallToothSegments : largeToothSegments;
  const backgroundImage = isSmallTooth ? smallToothBg : largeToothBg;

  return (
    <div className="summary-card-wrapper">
      <div className="summary-tooth-id-header">{toothId}</div>
      <div
        className="summary-diagram-container-realigned"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      >
        {data?.isMissing ? (
          <div className="missing-overlay-realigned">
            MISSING ({data.missingCode})
          </div>
        ) : (
          segments.map((segment) => {
            const paramType = getParameterTypeForSegment(segment.id);
            const value = data?.segments?.[segment.id]?.[paramType];
            const className = `summary-value-box-realigned segment-type-${paramType}`;

            if (!value) return null;

            return (
              <div
                key={segment.id}
                className={className}
                style={{ top: segment.top, left: segment.left }}
              >
                {value}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ToothSummaryCard;
